import { useNavigate } from "@tanstack/react-router";
import ENV from "../../env";

import { useEffect, useRef, useState } from "react";

import { Button, Checkbox, Divider, FormState, Icon, Input, tokens } from "@vintecc/components";
import { faLock, faSpinner, faUser } from "@vintecc/pro-light-svg-icons";
import { useRegisterStore } from "../../stores/registerStore";
import { ExternalLink } from "../ExternalLink/ExternalLink";
import Logo from "../LogoComponent/LogoComponent";
import MfaContent from "../MfaContent/MfaContent";
import MsLogo from "../MsLogo/MsLogo";
import { tryRetrieveCodeValuesFromUrl } from "../../helpers/registerCodeHelper";
import { RegisterData, createRegisterSchema } from "../../schemas/register.schema";
import { useAuthTranslation } from "../../services/culture/auth.translations";
import { useCommonTranslation } from "../../services/culture/common.translations";

enum MicrosoftState {
  Disabled,
  Enabled,
  Required,
}

export const RegisterComponent = () => {
  const { t: tAuth } = useAuthTranslation();
  const { t: tCommon } = useCommonTranslation();
  const registerSchema = createRegisterSchema(tAuth);
  const navigate = useNavigate();
  const [step, setStep] = useState<"password" | "mfa">("password");
  const touchedRef = useRef<{ [key: string]: boolean }>({});
  const { error, microsoftState, canRegister, setError, checkRegisterState } = useRegisterStore();

  const url = new URL(window.location.href);
  const codeValuesResult = tryRetrieveCodeValuesFromUrl(url);

  const [formData, setFormData] = useState<RegisterData>({
    Password: "",
    ConfirmPassword: "",
    MfaEnabled: codeValuesResult.data?.mfaEnabled || false,
    MfaCode: undefined,
    AcceptedTermsOfService: false,
    AcceptedPrivacyPolicy: false,
  });

  useEffect(() => {
    if (codeValuesResult?.success && codeValuesResult?.data) {
      checkRegisterState(codeValuesResult.data.username, codeValuesResult.data.code);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!codeValuesResult.success || !codeValuesResult.data) {
    navigate({
      to: `${ENV.BASE_PATH}/error`,
      search: { title: tAuth("errors.invalidRegistrationCode"), message: tAuth("errors.failedToDecodeRegistrationCode") },
    });
    return <></>;
  }

  const submitRegister = async (data: RegisterData) => {
    if (data?.MfaEnabled && step === "password") {
      setStep("mfa");
      return;
    }

    const registerData = {
      username: codeValuesResult.data!.username,
      password: data.Password,
      code: codeValuesResult.data!.code,
      mfaCode: data.MfaCode,
      acceptedTermsOfService: ENV.TERMS_AND_CONDITIONS_URL ? data.AcceptedTermsOfService : true,
      acceptedPrivacyPolicy: ENV.PRIVACY_POLICY_URL ? data.AcceptedPrivacyPolicy : true,
    };

    await fetch(`${ENV.API_SERVER}/register`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(registerData),
    })
      .then(async (response) => {
        switch (response.status) {
          case 200:
            return response.json();
          case 403:
            setError({ type: "key", key: "errors.registrationFailedTryAgain" });
            break;
          default:
            setError({ type: "raw", message: await response.text() });
        }
      })
      .then((data?) => {
        if (!data) return;

        if (data.redirectUri) {
          window.location.replace(data.redirectUri);
        }
      })
      .catch((error) => {
        setError({ type: "raw", message: error.message });
      });
  };

  const translatedError = error?.type === "key" ? tAuth(error.key) : error?.message;

  return (
    <div
      id="register"
      className="flex relative sm:items-center items-start justify-center xl:justify-end overflow-auto xl:px-64 border-solid border-10 xl:border-20 border-white p-8"
    >
      {/* Password step */}
      {step === "password" && (
        <>
          <FormState
            className="border-2 z-20 border-white p-12 sm:p-24 w-64 min-w-64 box-content space-y-8"
            defaultValue={formData}
            onSubmit={async (data) => {
              setFormData(data);
              submitRegister(data);
            }}
            validationSchema={registerSchema}
            validateOnChange={true}
          >
            {({ state, setState, errors, isSubmitting }) => {
              const emptyErrors = Object.keys(errors).length === 0;
              return (
                <>
                  <Logo />

                  {microsoftState !== MicrosoftState.Required && (
                    <div className="space-y-4">
                      <Input
                        label={tAuth("register.username")}
                        name="username"
                        testId="register-username-input"
                        value={codeValuesResult.data!.username}
                        disabled
                        startIcon={<Icon icon={faUser} color={tokens.color.grey[400]} />}
                      />

                      <Input
                        label={tAuth("register.password")}
                        name="password"
                        testId="register-password-input"
                        type="password"
                        value={state.Password}
                        autoComplete="new-password"
                        required
                        onChange={(e) => {
                          touchedRef.current.Password = true;
                          setState({ ...state, Password: e.target.value });
                        }}
                        helpText={errors.Password}
                        hideHelpText={!touchedRef.current.Password}
                        helpTextIntent="danger"
                        startIcon={<Icon icon={faLock} color={tokens.color.grey[400]} />}
                      />

                      <Input
                        label={tAuth("register.confirmPassword")}
                        name="password2"
                        testId="register-confirm-password-input"
                        required={true}
                        type="password"
                        value={state.ConfirmPassword}
                        autoComplete="new-password"
                        title={tAuth("register.passwordsMustMatch")}
                        onChange={(e) => {
                          touchedRef.current.ConfirmPassword = true;
                          setState({
                            ...state,
                            ConfirmPassword: e.target.value,
                          });
                        }}
                        helpText={errors.ConfirmPassword}
                        hideHelpText={!touchedRef.current.ConfirmPassword}
                        helpTextIntent="danger"
                        startIcon={<Icon icon={faLock} color={tokens.color.grey[400]} />}
                      />

                      <Checkbox
                        name="mfaEnabled"
                        testId="register-mfa-enabled-checkbox"
                        label={tAuth("register.enableMfa")}
                        disabled={codeValuesResult.data?.mfaEnabled}
                        checked={state?.MfaEnabled}
                        onChange={(checked) => setState({ ...state, MfaEnabled: checked })}
                      />

                      {(ENV.TERMS_AND_CONDITIONS_URL || ENV.PRIVACY_POLICY_URL) && (
                        <div className="flex flex-col gap-2 pt-2">
                          {ENV.TERMS_AND_CONDITIONS_URL && (
                            <Checkbox
                              name="acceptedTermsOfService"
                              testId="register-accept-terms-checkbox"
                              checked={state.AcceptedTermsOfService}
                              onChange={(checked) => setState({ ...state, AcceptedTermsOfService: checked })}
                              label={
                                <span>
                                  {tAuth("register.acceptThe")}{" "}
                                  <a
                                    href={ENV.TERMS_AND_CONDITIONS_URL}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-blue-500 hover:underline"
                                  >
                                    {tCommon("common.termsOfUse")}
                                  </a>
                                </span>
                              }
                            />
                          )}
                          {ENV.PRIVACY_POLICY_URL && (
                            <Checkbox
                              name="acceptedPrivacyPolicy"
                              testId="register-accept-privacy-checkbox"
                              checked={state.AcceptedPrivacyPolicy}
                              onChange={(checked) => setState({ ...state, AcceptedPrivacyPolicy: checked })}
                              label={
                                <span>
                                  {tAuth("register.acceptThe")}{" "}
                                  <a
                                    href={ENV.PRIVACY_POLICY_URL}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-blue-500 hover:underline"
                                  >
                                    {tCommon("common.privacyPolicy")}
                                  </a>
                                </span>
                              }
                            />
                          )}
                        </div>
                      )}

                      <p className="text-red-600" data-testid="register-error-message">
                        {translatedError}
                      </p>

                      {state?.MfaEnabled && (
                        <Button
                          testId="register-next-step-button"
                          disabled={
                            !canRegister ||
                            (!!ENV.TERMS_AND_CONDITIONS_URL && !state.AcceptedTermsOfService) ||
                            (!!ENV.PRIVACY_POLICY_URL && !state.AcceptedPrivacyPolicy)
                          }
                          fullWidth
                          className="register-btn"
                          variant="filled"
                          intent="default"
                        >
                          {tCommon("common.nextStep")}
                        </Button>
                      )}

                      <div>
                        {!state?.MfaEnabled && (
                          <Button
                            type="submit"
                            fullWidth
                            className="register-btn"
                            variant="filled"
                            intent="default"
                            testId="register-submit-button"
                            startIcon={isSubmitting ? <Icon icon={faSpinner} spin /> : undefined}
                            disabled={
                              isSubmitting ||
                              !canRegister ||
                              !emptyErrors ||
                              (!!ENV.TERMS_AND_CONDITIONS_URL && !state.AcceptedTermsOfService) ||
                              (!!ENV.PRIVACY_POLICY_URL && !state.AcceptedPrivacyPolicy)
                            }
                          >
                            {tCommon("common.register")}
                          </Button>
                        )}
                      </div>
                    </div>
                  )}
                  {microsoftState === MicrosoftState.Enabled && <Divider label={tAuth("common.or")} />}

                  {microsoftState >= MicrosoftState.Enabled && (
                    <Button
                      type="button"
                      fullWidth
                      className="ms-btn"
                      testId="register-microsoft-button"
                      startIcon={<MsLogo />}
                      disabled={
                        !canRegister ||
                        (!!ENV.TERMS_AND_CONDITIONS_URL && !state.AcceptedTermsOfService) ||
                        (!!ENV.PRIVACY_POLICY_URL && !state.AcceptedPrivacyPolicy)
                      }
                      onClick={() => {
                        const params = new URLSearchParams({
                          username: codeValuesResult.data!.username,
                          registerCode: codeValuesResult.data!.code,
                          acceptedTermsOfService: (ENV.TERMS_AND_CONDITIONS_URL ? state.AcceptedTermsOfService : true).toString(),
                          acceptedPrivacyPolicy: (ENV.PRIVACY_POLICY_URL ? state.AcceptedPrivacyPolicy : true).toString(),
                        });
                        window.location.replace(`${ENV.API_SERVER}/microsoft/register?${params.toString()}`);
                      }}
                    >
                      {tCommon("common.signUpWithMicrosoft")}
                    </Button>
                  )}

                  {ENV.DISCLOSURE_URL && (
                    <p className="text-xs text-grey-400 text-center pt-4">
                      {tAuth("register.securityQuestionsPrefix")}{" "}
                      <ExternalLink url={ENV.DISCLOSURE_URL} label={tCommon("common.responsibleDisclosure")} />.
                    </p>
                  )}
                </>
              );
            }}
          </FormState>
        </>
      )}

      {/* MFA step */}
      {step === "mfa" && (
        <>
          <FormState
            defaultValue={formData}
            onSubmit={(data) => submitRegister({ ...formData, MfaCode: data.MfaCode })}
            validationSchema={registerSchema}
            validateOnChange={true}
          >
            {({ state, setState, errors, isSubmitting }) => {
              const emptyErrors = Object.keys(errors).length === 0;

              return (
                <>
                  <Logo />

                  {microsoftState !== MicrosoftState.Required && (
                    <div className="space-y-4">
                      <MfaContent
                        testId="register-mfa-content"
                        username={codeValuesResult.data!.username}
                        registerCode={codeValuesResult.data!.code}
                        onChange={(mfa) => setState({ ...state, MfaCode: mfa.pinCode })}
                      />
                      <div>
                        <Button
                          type="submit"
                          fullWidth
                          className="register-btn"
                          variant="filled"
                          intent="default"
                          testId="register-mfa-submit-button"
                          startIcon={isSubmitting ? <Icon icon={faSpinner} spin /> : undefined}
                          disabled={isSubmitting || !canRegister || !emptyErrors}
                        >
                          {tCommon("common.register")}
                        </Button>
                      </div>

                      {ENV.DISCLOSURE_URL && (
                        <p className="text-xs text-grey-400 text-center pt-4">
                          {tAuth("register.securityQuestionsPrefix")}{" "}
                          <ExternalLink url={ENV.DISCLOSURE_URL} label={tCommon("common.responsibleDisclosure")} />.
                        </p>
                      )}
                    </div>
                  )}
                </>
              );
            }}
          </FormState>
        </>
      )}
    </div>
  );
};
